import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import WelcomeBackground from '../components/WelcomeBackground';
import profileAPI from '../api/profile';
import { useAuth } from '../contexts/AuthContext';
import ResumePreview from '../components/cv/ResumePreview';
import CompanyAutocomplete from '../components/common/CompanyAutocomplete';
import CatalogAutocomplete from '../components/common/CatalogAutocomplete';
import catalogAPI from '../api/catalog';
import {
  buildResumeModel,
  resumeModelHasContent,
  suggestDegreeAbbreviation,
  CATEGORY_TITLES
} from '../utils/resumeFormat';
// The whole flow guides the applicant to fill exactly what the Smart Matching
// Resume Specification v1.1 needs, in the spec's category order (§6). The output
// is a real PDF rendered by the server (POST /applicants/resume → wkhtmltopdf),
// which is the only path that gets Hebrew RTL right in a PDF. Identity
// (name/phone/email) comes from the applicant profile; the applicant only adds
// an optional LinkedIn.

const DRAFT_KEY = 'suitup_cv_draft_v2';
const RESUME_FILE_NAME = 'suitup_resume.pdf';
const RESUME_PDF_MIME = 'application/pdf';

const emptyEducation = { degreeName: '', degreeId: null, degreeAbbreviation: '', institution: '', institutionId: null, specialization: '', specializationId: null, startYear: '', endYear: '', average: '', honors: '' };
const emptyExperience = { company: '', role: '', startMonth: '', startYear: '', endMonth: '', endYear: '', current: false, bullets: [''] };
const emptySkill = { title: '', description: '', skillId: null };
const emptyLanguage = { language: '', languageId: null, reading: '', writing: '', verbal: '' };
const emptyMilitary = { unit: '', role: '', startYear: '', endYear: '' };
const emptyCourse = { year: '', name: '' };

const emptyData = () => ({
  linkedin: '',
  summary: '',
  education: [{ ...emptyEducation }],
  experience: [{ ...emptyExperience, bullets: [''] }],
  relevantSkills: [{ ...emptySkill }],
  languages: [{ ...emptyLanguage }],
  militaryService: [{ ...emptyMilitary }],
  computerSkills: [''],
  courses: [{ ...emptyCourse }]
});

// The spec's category order. Summary is the only required step; everything else
// is shown only if the applicant has something to say (§6 "empty category is not
// displayed").
const STEPS = [
  { name: 'summary', title: CATEGORY_TITLES.summary, icon: '✨', required: true,
    intro: 'פסקה אחת בגוף שלישי שמסכמת ניסיון, תחומי מומחיות ויתרון מקצועי מרכזי.',
    why: 'כדאי לפרט על עצמכם בשתי שורות. זה החלק הראשון שמעסיקים קוראים' },
  { name: 'education', title: CATEGORY_TITLES.education, icon: '🎓', required: false,
    intro: 'תארים והכשרות — מהחדש ביותר לישן ביותר.',
    why: 'תארים מוצגים עם הקיצור האקדמי; ממוצע יוצג רק אם הוא מעל 90.' },
  { name: 'experience', title: CATEGORY_TITLES.experience, icon: '💼', required: false },
  { name: 'relevantSkills', title: CATEGORY_TITLES.relevantSkills, icon: '⚡', required: false },
  { name: 'languages', title: CATEGORY_TITLES.languages, icon: '🌍', required: false,
    intro: 'שפה ורמת שליטה. יוצגו בשורה אחת.',
    why: 'הרבה משרות דורשות שפות ספציפיות.' },
  { name: 'militaryService', title: CATEGORY_TITLES.militaryService, icon: '🎖️', required: false,
    intro: 'יחידה ותפקיד, אם רלוונטי.',
    why: 'מוצג רק אם מולא.' },
  { name: 'computerSkills', title: CATEGORY_TITLES.computerSkills, icon: '🖥️', required: false,
    intro: 'כלים ומערכות. יוצגו בשורה אחת מופרדת בקו.',
    why: 'למשל Excel, Word, מערכות ייעודיות בתחום.' },
  { name: 'courses', title: CATEGORY_TITLES.courses, icon: '📜', required: false,
    intro: 'שנה ושם הקורס או ההסמכה.',
    why: 'מוצג רק אם מולא.' }
];

// Academic abbreviations offered in the dropdown (§9). Fixed list, no free text.
const ABBREVIATION_OPTIONS = ['LL.B.', 'LL.M.', 'B.A.', 'M.B.A.', 'B.Sc.', 'M.Sc.', 'M.D.'];

const inputClass = 'block w-full rounded-xl border-gray-200 bg-gray-50 px-4 py-3 text-sm shadow-none placeholder:text-gray-400 focus:border-yellow-500 focus:bg-white focus:ring-yellow-500';
const smallInputClass = 'block w-full rounded-xl border-gray-200 bg-gray-50 px-3 py-2 text-sm shadow-none placeholder:text-gray-400 focus:border-yellow-500 focus:bg-white focus:ring-yellow-500';

const toNum = (value) => {
  const n = parseInt(value, 10);
  return Number.isFinite(n) ? n : null;
};

// ---- input sanitizers ------------------------------------------------------
// Keep numeric fields numeric as the applicant types (also guards paste). Each
// returns the cleaned string to store back in state; empty is always allowed so
// a field can be cleared.
const onlyDigits = (value, maxLen) => String(value ?? '').replace(/\D/g, '').slice(0, maxLen);
// A 4-digit year (no century enforced here; range is validated on blur/review).
const sanitizeYear = (value) => onlyDigits(value, 4);
// A calendar month 1–12; clamps anything above 12 down to 12.
const sanitizeMonth = (value) => {
  const digits = onlyDigits(value, 2);
  if (!digits) return '';
  return parseInt(digits, 10) > 12 ? '12' : digits;
};
// A grade 0–100 with up to one decimal place.
const sanitizeGrade = (value) => {
  let cleaned = String(value ?? '').replace(/[^\d.]/g, '');
  const dot = cleaned.indexOf('.');
  if (dot !== -1) cleaned = cleaned.slice(0, dot + 1) + cleaned.slice(dot + 1).replace(/\./g, '');
  const [intPart = '', decPart] = cleaned.split('.');
  let result = intPart.slice(0, 3);
  if (Number(result) > 100) result = '100';
  if (decPart !== undefined) result += '.' + decPart.slice(0, 1);
  return result;
};
// A year is plausible when it falls in a sane working range. Used only for a
// soft warning — the value is still stored so the applicant isn't blocked.
const YEAR_MIN = 1950;
const CURRENT_YEAR = new Date().getFullYear();
const yearOutOfRange = (value) => {
  const n = toNum(value);
  return n != null && String(value).length === 4 && (n < YEAR_MIN || n > CURRENT_YEAR);
};

// Map a server work-history entry (applicants_history_companies) to an editable
// experience row. `historyId` remembers the source so we can update it later.
const experienceFromHistory = (entry) => ({
  ...emptyExperience,
  historyId: entry.id,
  company: entry.history_company?.name || '',
  role: entry.job_title || '',
  startMonth: entry.month_start ? String(entry.month_start) : '',
  startYear: entry.year_start ? String(entry.year_start) : '',
  endMonth: entry.month_end ? String(entry.month_end) : '',
  endYear: entry.year_end ? String(entry.year_end) : '',
  current: Boolean(entry.still_working),
  bullets: ['']
});

// Map an editable experience row to work-history params. Passing `name` lets the
// server find-or-create the HistoryCompany; a `current` job clears end dates.
const historyParamsFromExperience = (entry) => ({
  name: entry.company.trim(),
  job_title: entry.role.trim(),
  year_start: toNum(entry.startYear),
  month_start: toNum(entry.startMonth),
  year_end: entry.current ? null : toNum(entry.endYear),
  month_end: entry.current ? null : toNum(entry.endMonth),
  still_working: Boolean(entry.current)
});

// Map a server education entry (applicants_educations) to an editable row.
const educationFromServer = (entry) => ({
  ...emptyEducation,
  educationId: entry.id,
  degreeName: entry.education_degree?.name || '',
  degreeId: entry.education_degree?.id || null,
  // The abbreviation has no server column; re-derive it from the degree name.
  degreeAbbreviation: suggestDegreeAbbreviation(entry.education_degree?.name || '') || '',
  institution: entry.education_institution?.name || '',
  institutionId: entry.education_institution?.id || null,
  specialization: entry.education_course?.name || '',
  specializationId: entry.education_course?.id || null,
  startYear: entry.year_start ? String(entry.year_start) : '',
  endYear: entry.year_end ? String(entry.year_end) : '',
  average: entry.grade != null ? String(entry.grade) : '',
  honors: entry.with_honors ? 'הצטיינות' : ''
});

// Map an editable education row to applicants_educations params. Only rows that
// matched a catalog degree/institution can be persisted (the applicant can't
// create catalog rows). Returns null when there is nothing to save.
const educationParamsFromRow = (row) => {
  if (!row.degreeId && !row.institutionId && !row.specializationId) return null;
  return {
    education_degree_id: row.degreeId || undefined,
    education_institution_id: row.institutionId || undefined,
    education_course_id: row.specializationId || undefined,
    year_start: toNum(row.startYear) ?? undefined,
    year_end: toNum(row.endYear) ?? undefined,
    grade: row.average ? Number(row.average) : undefined,
    with_honors: Boolean(row.honors && row.honors.trim())
  };
};

// Map a server language entry (applicants_languages) to an editable row.
const languageFromServer = (entry) => ({
  ...emptyLanguage,
  languageRecordId: entry.id,
  language: entry.language?.name || '',
  languageId: entry.language?.id || null,
  reading: entry.reading != null ? String(entry.reading) : '',
  writing: entry.writing != null ? String(entry.writing) : '',
  verbal: entry.verbal != null ? String(entry.verbal) : ''
});

// Language proficiency levels. `value` is the integer the server stores in
// reading/writing/verbal; `label` mirrors the server's display thresholds
// (get_single_display_text). Used for the wizard's three selects and for the
// resume display text.
const LANGUAGE_LEVELS = [
  { value: 8, label: 'שפת אם' },
  { value: 7, label: 'כשפת אם' },
  { value: 6, label: 'רמה גבוהה' },
  { value: 4, label: 'רמה בינונית' },
  { value: 2, label: 'רמה נמוכה' }
];
// Server display text from a stored integer (mirrors get_single_display_text).
const levelTextFromInt = (level) => {
  const n = Number(level);
  if (!Number.isFinite(n)) return '';
  if (n >= 8) return 'שפת אם';
  if (n >= 7) return 'כשפת אם';
  if (n >= 6) return 'רמה גבוהה';
  if (n >= 4) return 'רמה בינונית';
  return 'רמה נמוכה';
};

// Find a catalog row ({id,name}) whose name matches `value` (case-insensitive).
const matchCatalogId = (options, value) => {
  const term = (value || '').trim().toLowerCase();
  if (!term) return null;
  const hit = (options || []).find((option) => (option.name || '').trim().toLowerCase() === term);
  return hit ? hit.id : null;
};

const withDefaults = (saved) => {
  const base = emptyData();
  const merged = { ...base, ...saved };
  // Guarantee every list has at least one editable row.
  ['education', 'experience', 'relevantSkills', 'languages', 'militaryService', 'courses'].forEach((key) => {
    if (!Array.isArray(merged[key]) || merged[key].length === 0) merged[key] = base[key];
  });
  merged.experience = merged.experience.map((entry) => ({
    ...emptyExperience,
    ...entry,
    bullets: Array.isArray(entry.bullets) && entry.bullets.length ? entry.bullets : ['']
  }));
  if (!Array.isArray(merged.computerSkills) || merged.computerSkills.length === 0) merged.computerSkills = [''];
  return merged;
};

const loadDraft = () => {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    return raw ? withDefaults(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
};

const CreateCV = () => {
  const draftRef = useRef(loadDraft());
  const applicantIdRef = useRef(null);
  const [data, setData] = useState(() => draftRef.current || emptyData());
  const [identity, setIdentity] = useState({ fullName: '', phone: '', email: '', linkedin: '' });
  // Full skills catalog (id+name). Free-text relevant-skill titles are matched
  // against it so a matching skill can be saved to applicants_skills.
  const [skillCatalog, setSkillCatalog] = useState([]);
  const [catalogs, setCatalogs] = useState({ institutions: [], degrees: [], courses: [], languages: [] });
  const [started, setStarted] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  // Which card is expanded per accordion section (skills / education /
  // experience). Only one open at a time keeps each step compact; a newly added
  // row opens itself for editing.
  const [openSkill, setOpenSkill] = useState(0);
  const [openEducation, setOpenEducation] = useState(0);
  const [openExperience, setOpenExperience] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [savedAt, setSavedAt] = useState(false);
  const [error, setError] = useState('');
  const isFirstSave = useRef(true);
  const { currentUser } = useAuth() || {};
  const location = useLocation();
  const navigate = useNavigate();
  const nextPage = location.state?.returnTo || '/jobs';

  const isReview = stepIndex >= STEPS.length;
  const step = STEPS[stepIndex];

  // The resume formatter shows one level per language; derive it from the
  // three stored levels (spoken first, it best reflects everyday proficiency).
  const resumeLanguages = (data.languages || []).map((lang) => ({
    language: lang.language,
    level: levelTextFromInt(lang.verbal || lang.writing || lang.reading)
  }));
  const model = buildResumeModel({ ...data, languages: resumeLanguages });
  const hasContent = resumeModelHasContent(model);
  const previewIdentity = { ...identity, linkedin: data.linkedin };

  // Pull identity + sensible prefills from the applicant profile. A saved draft
  // always wins over prefills so we never clobber the applicant's own edits.
  useEffect(() => {
    let active = true;
    const loadProfile = async () => {
      try {
        let applicantId = currentUser?.applicant_id || currentUser?.resource?.id;
        let userData = currentUser;
        if (!applicantId) {
          const response = await profileAPI.getUserData();
          userData = response.data;
          applicantId = response.data?.resource?.id;
        }
        if (!applicantId) return;
        applicantIdRef.current = applicantId;

        const emptyData_ = { data: [] };
        const [
          profileResponse, skillsResponse, historyResponse,
          institutionsRes, degreesRes, coursesRes, languagesCatalogRes, skillsCatalogRes,
          educationsRes, languagesRes, militaryRes, militaryStartRes, militaryEndRes, computerRes
        ] = await Promise.all([
          profileAPI.getProfile(applicantId),
          profileAPI.getApplicantSkills().catch(() => emptyData_),
          profileAPI.getWorkHistory().catch(() => emptyData_),
          catalogAPI.getInstitutions().catch(() => emptyData_),
          catalogAPI.getDegrees().catch(() => emptyData_),
          catalogAPI.getCourses().catch(() => emptyData_),
          catalogAPI.getLanguages().catch(() => emptyData_),
          profileAPI.getSkills().catch(() => emptyData_),
          profileAPI.getEducations().catch(() => emptyData_),
          profileAPI.getLanguages().catch(() => emptyData_),
          catalogAPI.getResourceText('military').catch(() => ({ data: null })),
          catalogAPI.getResourceText('military_year_start').catch(() => ({ data: null })),
          catalogAPI.getResourceText('military_year_end').catch(() => ({ data: null })),
          catalogAPI.getResourceText('computer_skills').catch(() => ({ data: null }))
        ]);
        if (!active) return;

        const asArray = (res) => (Array.isArray(res.data) ? res.data : []);
        const workHistory = asArray(historyResponse);
        const serverEducations = asArray(educationsRes);
        const serverLanguages = asArray(languagesRes);
        setCatalogs({
          institutions: asArray(institutionsRes),
          degrees: asArray(degreesRes),
          courses: asArray(coursesRes),
          languages: asArray(languagesCatalogRes)
        });
        setSkillCatalog(asArray(skillsCatalogRes));
        // Military lives as up to three resource_texts; the combined content is a
        // single free-text block (per the "merge into one" decision).
        const militaryContent = militaryRes?.data?.content || '';
        const militaryStart = militaryStartRes?.data?.content || '';
        const militaryEnd = militaryEndRes?.data?.content || '';
        // Computer skills live as a single resource_text, joined by " | ".
        const computerContent = computerRes?.data?.content || '';

        const profile = profileResponse.data || {};
        // The user fields (name/email/phone) live on profile.user; when the API
        // omits them, currentUser / the /applicants/user payload carry them at the
        // top level (never under `.resource`, which is the applicant record).
        let user = profile.user || userData || {};
        if (!user.full_name && !user.first_name && !user.email && !user.phone_number) {
          try {
            const fetched = await profileAPI.getUserData();
            if (fetched?.data) user = fetched.data;
          } catch {
            /* keep whatever we have */
          }
        }
        if (!active) return;
        const fullName = user.full_name || [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
        setIdentity({
          fullName: fullName || '',
          phone: user.phone_number || user.phone || '',
          email: user.email || '',
          linkedin: ''
        });

        // The applicant's stored skills are merged straight into the skill rows
        // below (full sync), so there is no separate "pick from my skills" step.
        const applicantSkills = (skillsResponse.data || [])
          .map((item) => ({
            title: item.skill?.name || item.skill_name || item.name || '',
            description: item.applicant_comment || '',
            skillId: item.skill?.id || item.skill_id || null
          }))
          .filter((item) => item.title);

        // Seed every section the applicant already has data for, so all existing
        // profile data shows up automatically in the wizard. Each section fills
        // only when it is still empty, so a saved draft's own edits are never
        // clobbered — even a returning applicant sees server data in the sections
        // they hadn't touched yet.
        setData((current) => {
          const next = { ...current };
          if (!next.summary && profile.about_me) next.summary = profile.about_me;
          // Show the applicant their saved work history in the experience step.
          // Fall back to the profile's current company only when no history exists.
          const experienceEmpty = next.experience.every((e) => !e.company && !e.role);
          if (workHistory.length && experienceEmpty) {
            next.experience = workHistory.map(experienceFromHistory);
          } else if (profile.current_company && next.experience.length && !next.experience[0].company) {
            next.experience = next.experience.map((entry, index) => (
              index === 0 ? { ...entry, company: profile.current_company } : entry
            ));
          }
          // Education from applicants_educations.
          const educationEmpty = next.education.every((e) => !e.degreeName && !e.institution);
          if (serverEducations.length && educationEmpty) {
            next.education = serverEducations.map(educationFromServer);
          }
          // Languages from applicants_languages.
          const languagesEmpty = next.languages.every((l) => !l.language);
          if (serverLanguages.length && languagesEmpty) {
            next.languages = serverLanguages.map(languageFromServer);
          }
          // Military — the combined text goes into the first row's unit field
          // (structure can't be recovered from the merged content).
          const militaryEmpty = next.militaryService.every((m) => !m.unit && !m.role);
          if ((militaryContent || militaryStart || militaryEnd) && militaryEmpty) {
            next.militaryService = [{
              ...emptyMilitary,
              unit: militaryContent,
              startYear: militaryStart,
              endYear: militaryEnd
            }];
          }
          // Merge the applicant's stored skills in per-title: fill a matching
          // row's missing description from applicant_comment, and append skills
          // not present yet. This way a detail the applicant added later (e.g. in
          // the profile) shows up even when a draft already holds the skill name,
          // without clobbering a description they typed here.
          if (applicantSkills.length) {
            const rows = next.relevantSkills
              .filter((item) => item.title || item.description)
              .map((item) => ({ ...item }));
            const byTitle = new Map(rows.map((r) => [r.title.trim().toLowerCase(), r]));
            applicantSkills.forEach((skill) => {
              const key = skill.title.trim().toLowerCase();
              const existing = byTitle.get(key);
              if (existing) {
                if (!existing.description && skill.description) existing.description = skill.description;
                if (!existing.skillId && skill.skillId) existing.skillId = skill.skillId;
              } else {
                const added = { ...skill };
                rows.push(added);
                byTitle.set(key, added);
              }
            });
            if (rows.length) next.relevantSkills = rows;
          }
          // Computer skills — split the single resource_text back into rows.
          const computerEmpty = next.computerSkills.every((s) => !s.trim());
          if (computerContent && computerEmpty) {
            const parts = computerContent.split('|').map((s) => s.trim()).filter(Boolean);
            if (parts.length) next.computerSkills = parts;
          }
          return next;
        });
      } catch (err) {
        console.error('Failed to load profile for resume prefill:', err);
      }
    };

    if (currentUser) loadProfile();
    return () => { active = false; };
  }, [currentUser]);

  // Autosave every change so nothing is lost between sessions.
  useEffect(() => {
    if (isFirstSave.current) {
      isFirstSave.current = false;
      return;
    }
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(data));
      setSavedAt(true);
    } catch {
      /* storage unavailable — keep working without a draft */
    }
  }, [data]);

  // ---- state helpers -------------------------------------------------------
  const setField = (key, value) => setData((current) => ({ ...current, [key]: value }));
  const patchList = (key, index, patch) => setData((current) => ({
    ...current,
    [key]: current[key].map((entry, i) => (i === index ? { ...entry, ...patch } : entry))
  }));
  const addToList = (key, factory) => setData((current) => ({ ...current, [key]: [...current[key], factory()] }));
  const removeFromList = (key, index, factory) => setData((current) => {
    const next = current[key].filter((_, i) => i !== index);
    return { ...current, [key]: next.length ? next : [factory()] };
  });

  const patchBullet = (expIndex, bulletIndex, value) => setData((current) => ({
    ...current,
    experience: current.experience.map((entry, i) => (
      i === expIndex ? { ...entry, bullets: entry.bullets.map((b, j) => (j === bulletIndex ? value : b)) } : entry
    ))
  }));
  const addBullet = (expIndex) => setData((current) => ({
    ...current,
    experience: current.experience.map((entry, i) => (
      i === expIndex ? { ...entry, bullets: [...entry.bullets, ''] } : entry
    ))
  }));
  const removeBullet = (expIndex, bulletIndex) => setData((current) => ({
    ...current,
    experience: current.experience.map((entry, i) => {
      if (i !== expIndex) return entry;
      const bullets = entry.bullets.filter((_, j) => j !== bulletIndex);
      return { ...entry, bullets: bullets.length ? bullets : [''] };
    })
  }));

  // Persist a step's data to the server as soon as the applicant leaves it, so
  // nothing is lost if they never reach the final submit. Best-effort.
  const persisters = {
    summary: () => persistSummary(),
    experience: () => persistWorkHistory(),
    education: () => persistEducation(),
    relevantSkills: () => persistSkills(),
    languages: () => persistLanguages(),
    militaryService: () => persistMilitary(),
    computerSkills: () => persistComputerSkills()
  };
  const saveStep = (name) => {
    const persist = persisters[name];
    if (!persist) return;
    persist()
      .then((failed) => {
        if (failed) setError('חלק מהמידע לא נשמר. בדקו שהשדות תקינים (למשל תאריכים).');
      })
      .catch(() => {});
  };

  const goNext = () => {
    setError('');
    saveStep(step?.name);
    setStepIndex((current) => Math.min(current + 1, STEPS.length));
  };
  const goBack = () => { setError(''); setStepIndex((current) => Math.max(current - 1, 0)); };
  const jumpTo = (index) => { setError(''); setStepIndex(index); };

  // Persist the applicant's work history (applicants_history_companies). The
  // wizard holds the full desired list, so it just sends that to the server's
  // sync endpoint, which updates known rows (matched by id), creates new ones and
  // deletes any it no longer sees — a removed row is actually removed. Returns
  // the number of rows that failed to save (0 or all, since sync is atomic).
  const persistWorkHistory = async () => {
    const entries = data.experience
      .filter((entry) => entry.company.trim())
      .map((entry) => ({ id: entry.historyId || undefined, ...historyParamsFromExperience(entry) }));
    try {
      await profileAPI.syncWorkHistory(entries);
      return 0;
    } catch (err) {
      console.error('Failed to save work history:', err);
      return entries.length || 1;
    }
  };

  // Persist education rows that matched a catalog degree/institution/course. The
  // server de-dupes by (institution, degree, course), so re-saving updates in
  // place. Unmatched free-text rows have no ids and are skipped (resume-only).
  const persistEducation = async () => {
    const rows = data.education
      .map((row) => ({ row, params: educationParamsFromRow(row) }))
      .filter((item) => item.params);
    if (!rows.length) return 0;

    let existing = [];
    try {
      const res = await profileAPI.getEducations();
      existing = Array.isArray(res.data) ? res.data : [];
    } catch {
      existing = [];
    }
    const byId = new Map(existing.map((e) => [e.id, e]));

    let failed = 0;
    for (const { row, params } of rows) {
      const prior = row.educationId && byId.get(row.educationId);
      try {
        if (prior) await profileAPI.updateEducation(prior.id, params);
        else await profileAPI.addEducation(params);
      } catch (err) {
        failed += 1;
        console.error('Failed to save education entry:', err);
      }
    }
    return failed;
  };

  // Persist language rows that matched a catalog language. The server de-dupes by
  // language_id. reading/writing/verbal are stored as integers.
  const persistLanguages = async () => {
    const rows = data.languages.filter((lang) => lang.languageId);
    if (!rows.length) return 0;

    let existing = [];
    try {
      const res = await profileAPI.getLanguages();
      existing = Array.isArray(res.data) ? res.data : [];
    } catch {
      existing = [];
    }
    const byLanguageId = new Map(existing.map((l) => [l.language?.id, l]));

    let failed = 0;
    for (const row of rows) {
      const params = {
        language_id: row.languageId,
        reading: toNum(row.reading) ?? undefined,
        writing: toNum(row.writing) ?? undefined,
        verbal: toNum(row.verbal) ?? undefined
      };
      const prior = existing.find((e) => e.id === row.languageRecordId) || byLanguageId.get(row.languageId);
      try {
        if (prior) await profileAPI.updateLanguage(prior.id, params);
        else await profileAPI.addLanguage(params);
      } catch (err) {
        failed += 1;
        console.error('Failed to save language entry:', err);
      }
    }
    return failed;
  };

  // Persist the summary into applicant.about_me. Returns 1 on failure, 0 otherwise.
  const persistSummary = async () => {
    const applicantId = applicantIdRef.current;
    if (!applicantId) return 0;
    try {
      await profileAPI.updateProfile(applicantId, { about_me: (data.summary || '').trim() });
      return 0;
    } catch (err) {
      console.error('Failed to save summary (about_me):', err);
      return 1;
    }
  };

  // Persist relevant skills into applicants_skills. The server keys on skill_id
  // (a catalog Skill), so only rows whose title matches a catalog skill can be
  // saved; the description goes into applicant_comment. Unmatched free-text rows
  // are resume-only and skipped. Returns the number of rows that failed to save.
  const persistSkills = async () => {
    const rows = data.relevantSkills
      .map((row) => ({ row, skillId: row.skillId ?? matchCatalogId(skillCatalog, row.title) }))
      .filter((item) => item.skillId);
    if (!rows.length) return 0;

    let failed = 0;
    for (const { row, skillId } of rows) {
      try {
        await profileAPI.addApplicantSkill(skillId, { applicant_comment: (row.description || '').trim() });
      } catch (err) {
        failed += 1;
        console.error('Failed to save applicant skill:', err);
      }
    }
    return failed;
  };

  // Persist computer skills into a single `computer_skills` resource_text, joined
  // by " | " (matching the resume one-line format). An empty list clears it.
  const persistComputerSkills = async () => {
    const content = (data.computerSkills || [])
      .map((s) => (s || '').trim())
      .filter(Boolean)
      .join(' | ');
    try {
      if (content) await catalogAPI.saveResourceText('computer_skills', content);
      else await catalogAPI.deleteResourceText('computer_skills').catch(() => {});
      return 0;
    } catch (err) {
      console.error('Failed to save computer_skills resource_text:', err);
      return 1;
    }
  };

  // Persist military service. All rows are merged into a single `military`
  // resource_text, with the earliest start and latest end kept as separate
  // designators (the schema holds one designator per applicant).
  const persistMilitary = async () => {
    const rows = data.militaryService.filter((m) => (m.unit && m.unit.trim()) || (m.role && m.role.trim()));
    const content = rows
      .map((m) => [m.unit, m.role].map((x) => (x || '').trim()).filter(Boolean).join(' – '))
      .filter(Boolean)
      .join('\n');
    const withYears = rows.filter((m) => m.startYear || m.endYear);
    const yearStart = withYears.length ? withYears[0].startYear : '';
    const yearEnd = withYears.length ? withYears[withYears.length - 1].endYear : '';
    if (!content && !yearStart && !yearEnd) return 0;

    let failed = 0;
    const save = async (designator, value) => {
      try {
        if (value && String(value).trim()) await catalogAPI.saveResourceText(designator, String(value).trim());
        else await catalogAPI.deleteResourceText(designator).catch(() => {});
      } catch (err) {
        failed += 1;
        console.error('Failed to save military resource_text:', err);
      }
    };
    await save('military', content);
    await save('military_year_start', yearStart);
    await save('military_year_end', yearEnd);
    return failed;
  };

  // ---- final generation ----------------------------------------------------
  const createResume = async (event) => {
    event?.preventDefault();
    if (!hasContent) {
      setError('יש להזין לפחות שדה אחד. אפשר להתחיל מכל חלק שנוח לכם 😊');
      return;
    }
    try {
      setIsLoading(true);
      setError('');
      // The server renders the PDF (wkhtmltopdf) from the same model the preview
      // shows, and takes the name/phone/email from the profile itself — only the
      // applicant-supplied LinkedIn has to travel with it.
      const response = await profileAPI.createResume({ ...model, linkedin: data.linkedin });
      const file = new File([response.data], RESUME_FILE_NAME, { type: RESUME_PDF_MIME });
      await profileAPI.uploadCV(file);
      // Save all structured sections back to the applicant's profile.
      await Promise.all([
        persistSummary(),
        persistWorkHistory(),
        persistEducation(),
        persistSkills(),
        persistLanguages(),
        persistMilitary(),
        persistComputerSkills()
      ]);

      try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }

      const url = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.name;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      navigate(nextPage);
    } catch (err) {
      console.error('Error creating resume:', err);
      setError('לא הצלחנו ליצור את קורות החיים. יש לנסות שוב.');
      setIsLoading(false);
    }
  };

  const stepFilled = (name) => {
    if (name === 'summary') return Boolean(model.summary);
    if (name === 'languages') return Boolean(model.languages);
    if (name === 'computerSkills') return Boolean(model.computerSkills);
    return Array.isArray(model[name]) && model[name].length > 0;
  };

  // ---- welcome screen ------------------------------------------------------
  if (!started) {
    return (
      <Layout>
        <main className="relative flex min-h-[calc(100vh-4rem)] items-center overflow-hidden bg-[#fbf4ea] px-4 py-8 sm:px-6">
          <WelcomeBackground showScales={false} />
          <div className="relative mx-auto w-full max-w-md text-center">
            <span className="text-6xl" aria-hidden="true">👋</span>
            <h1 className="mt-4 text-3xl font-bold text-gray-900 sm:text-4xl">בואו נבנה את קורות החיים שלכם</h1>
            <p className="mt-4 text-lg text-gray-600">נלווה אתכם למלא את הפרטים הנכונים, והקובץ ייווצר בפורמט PDF מקצועי ואחיד.</p>
            <p className="mt-1 text-gray-500">אפשר לעצור בכל שלב ולחזור מאוחר יותר — הכול נשמר אוטומטית.</p>
            <button type="button" onClick={() => setStarted(true)} className="btn-primary mt-8 w-full rounded-xl py-3.5 text-base">
              {hasContent ? 'המשך מאיפה שהפסקתי' : 'בואו נתחיל'}
            </button>
            {hasContent && (
              <button
                type="button"
                onClick={() => { draftRef.current = null; setData(emptyData()); setStepIndex(0); }}
                className="mt-3 text-sm font-bold text-gray-500 hover:text-gray-700"
              >
                התחלה מחדש
              </button>
            )}
          </div>
        </main>
      </Layout>
    );
  }

  // ---- wizard header -------------------------------------------------------
  const stepNumber = isReview ? STEPS.length : stepIndex + 1;
  const percent = Math.round((isReview ? STEPS.length : stepIndex) / STEPS.length * 100);
  const header = (
    <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-gray-100">
      <div className="flex items-center justify-between text-sm">
        <span className="font-bold text-gray-800">{isReview ? 'סקירה והגשה' : `שלב ${stepNumber} מתוך ${STEPS.length}`}</span>
        <span className="flex items-center gap-2 text-gray-500">
          {savedAt && <span className="text-green-600" aria-live="polite">✓ נשמר</span>}
          <span>{percent}%</span>
        </span>
      </div>
      <div className="mt-3 h-2 overflow-hidden rounded-full bg-gray-100">
        <div className="h-full rounded-full bg-yellow-500 transition-all duration-300" style={{ width: `${percent}%` }} />
      </div>
    </div>
  );

  // ---- per-step editors ----------------------------------------------------
  const removeButton = (onClick, label) => (
    <button type="button" onClick={onClick} aria-label={label} className="text-sm font-bold text-gray-400 hover:text-red-600">
      הסרה
    </button>
  );
  const addButton = (onClick, label) => (
    <button type="button" onClick={onClick} className="text-sm font-bold text-yellow-700">{label}</button>
  );

  const pencilIcon = (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden="true">
      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
    </svg>
  );
  const trashIcon = (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden="true">
      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
    </svg>
  );
  const iconButton = (onClick, label, icon, danger) => (
    <button type="button" onClick={onClick} aria-label={label}
      className={`shrink-0 rounded-lg p-1.5 ${danger ? 'text-gray-400 hover:bg-red-50 hover:text-red-600' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-700'}`}>
      {icon}
    </button>
  );

  // Shared collapsible card for the skills / education / experience steps: a
  // compact header (chevron + title + subtitle/status) with edit + delete icons,
  // and the full editor revealed only when open.
  const accordionCard = ({ isOpen, title, placeholder, subtitle, status, onToggle, onEdit, onRemove, editLabel, removeLabel }, children) => (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-gray-50">
      <div className="flex items-center gap-1 px-3 py-2.5">
        <button type="button" aria-expanded={isOpen} onClick={onToggle}
          className="flex min-w-0 flex-1 items-center gap-2 text-right">
          <span aria-hidden="true" className={`shrink-0 text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`}>▸</span>
          <span className="min-w-0 flex-1">
            <span className={`block truncate text-sm font-bold ${title ? 'text-gray-800' : 'text-gray-400'}`}>{title || placeholder}</span>
            {subtitle && <span className="block truncate text-xs text-gray-400">{subtitle}</span>}
          </span>
          {status}
        </button>
        {iconButton(onEdit, editLabel, pencilIcon, false)}
        {iconButton(onRemove, removeLabel, trashIcon, true)}
      </div>
      {isOpen && <div className="grid gap-3 border-t border-gray-200 p-3">{children}</div>}
    </div>
  );

  // Remove a row from an accordion list and keep the open index sane.
  const removeAccordionRow = (key, index, factory, open, setOpen) => {
    removeFromList(key, index, factory);
    if (open >= index) setOpen((o) => Math.max(0, o - 1));
  };

  const renderSummary = () => (
    <div className="mt-4 space-y-3">
      <textarea
        aria-label={CATEGORY_TITLES.summary}
        rows={4}
        maxLength={2000}
        value={data.summary}
        onChange={(event) => setField('summary', event.target.value)}
        placeholder='למשל: עו"ד לאחר התמחות, בעלת ניסיון בתחומי האשראי, ניהול הסיכונים והמשפט המסחרי. בעלת יכולת ניתוח גבוהה וניסיון בסביבה רגולטורית מורכבת.'
        className={`resize-y leading-6 ${inputClass}`}
      />
      <div>
        <label htmlFor="linkedin" className="mb-1 block text-sm font-bold text-gray-700">LinkedIn (אופציונלי)</label>
        <input
          id="linkedin"
          aria-label="LinkedIn"
          inputMode="url"
          value={data.linkedin}
          onChange={(event) => setField('linkedin', event.target.value)}
          placeholder="linkedin.com/in/username"
          className={inputClass}
        />
        {data.linkedin.trim() && !/linkedin\.com\/.+/i.test(data.linkedin.trim()) && (
          <p className="mt-1 text-xs text-amber-600">כתובת LinkedIn נראית חלקית — למשל linkedin.com/in/username</p>
        )}
        <p className="mt-1 text-xs text-gray-400">השם, הטלפון והאימייל נלקחים אוטומטית מהפרופיל שלכם.</p>
      </div>
    </div>
  );

  const renderEducation = () => (
    <div className="mt-4 space-y-2">
      {data.education.map((item, index) => {
        const suggestion = suggestDegreeAbbreviation(item.degreeName);
        // Only a known abbreviation from the fixed list is allowed (no free text),
        // so it is always reproducible from the degree and needs no server column.
        // Fall back to the one auto-suggested from the degree name.
        const selectedAbbreviation = ABBREVIATION_OPTIONS.includes(item.degreeAbbreviation)
          ? item.degreeAbbreviation
          : (suggestion || '');
        const isOpen = openEducation === index;
        const subtitle = [item.institution, item.endYear].filter((x) => x && String(x).trim()).join(' · ');
        return (
          <div key={index}>
            {accordionCard({
              isOpen,
              title: item.degreeName.trim(),
              placeholder: `לימודים ${index + 1}`,
              subtitle,
              onToggle: () => setOpenEducation(isOpen ? -1 : index),
              onEdit: () => setOpenEducation(index),
              onRemove: () => removeAccordionRow('education', index, () => ({ ...emptyEducation }), openEducation, setOpenEducation),
              editLabel: `עריכת לימודים ${index + 1}`,
              removeLabel: `הסרת לימודים ${index + 1}`
            }, (
              <>
                <CatalogAutocomplete
                  ariaLabel={`שם התואר ${index + 1}`}
                  value={item.degreeName}
                  options={catalogs.degrees}
                  onChange={(name, id) => patchList('education', index, { degreeName: name, degreeId: id ?? matchCatalogId(catalogs.degrees, name) })}
                  placeholder="שם התואר, למשל: תואר ראשון במשפטים" className={inputClass} />
                <div>
                  <label className="mb-1 block text-sm font-bold text-gray-700">קיצור אקדמי</label>
                  <select aria-label={`קיצור אקדמי ${index + 1}`} value={selectedAbbreviation}
                    onChange={(event) => patchList('education', index, { degreeAbbreviation: event.target.value })} className={inputClass}>
                    <option value="">בחרו קיצור</option>
                    {ABBREVIATION_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
                  </select>
                </div>
                <CatalogAutocomplete
                  ariaLabel={`מוסד לימודים ${index + 1}`}
                  value={item.institution}
                  options={catalogs.institutions}
                  onChange={(name, id) => patchList('education', index, { institution: name, institutionId: id ?? matchCatalogId(catalogs.institutions, name) })}
                  placeholder="מוסד, למשל: אוניברסיטת תל אביב" className={inputClass} />
                <CatalogAutocomplete
                  ariaLabel={`התמחות ${index + 1}`}
                  value={item.specialization}
                  options={catalogs.courses}
                  onChange={(name, id) => patchList('education', index, { specialization: name, specializationId: id ?? matchCatalogId(catalogs.courses, name) })}
                  placeholder="התמחות / מסלול (אופציונלי)" className={inputClass} />
                {(item.degreeName.trim() || item.institution.trim() || item.specialization.trim()) &&
                  !item.degreeId && !item.institutionId && !item.specializationId && (
                  <p className="text-xs text-amber-600">בחרו תואר/מוסד/התמחות מהרשימה — ערך שאינו במאגר לא יישמר בפרופיל.</p>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <input aria-label={`שנת התחלה ${index + 1}`} inputMode="numeric" value={item.startYear}
                    onChange={(event) => patchList('education', index, { startYear: sanitizeYear(event.target.value) })}
                    placeholder="משנה, למשל 2019" className={smallInputClass} />
                  <input aria-label={`שנת סיום ${index + 1}`} inputMode="numeric" value={item.endYear}
                    onChange={(event) => patchList('education', index, { endYear: sanitizeYear(event.target.value) })}
                    placeholder="עד שנה, למשל 2023" className={smallInputClass} />
                </div>
                {(yearOutOfRange(item.startYear) || yearOutOfRange(item.endYear)) && (
                  <p className="text-xs text-amber-600">בדקו את השנים — הערך נראה חריג (טווח סביר {YEAR_MIN}–{CURRENT_YEAR}).</p>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <input aria-label={`ממוצע ${index + 1}`} inputMode="decimal" value={item.average}
                    onChange={(event) => patchList('education', index, { average: sanitizeGrade(event.target.value) })}
                    placeholder="ממוצע 0–100 (אופציונלי)" className={smallInputClass} />
                  <input aria-label={`הצטיינות ${index + 1}`} value={item.honors}
                    onChange={(event) => patchList('education', index, { honors: event.target.value })}
                    placeholder="הצטיינות (אופציונלי)" className={smallInputClass} />
                </div>
              </>
            ))}
          </div>
        );
      })}
      {addButton(() => {
        addToList('education', () => ({ ...emptyEducation }));
        setOpenEducation(data.education.length);
      }, '+ הוספת לימודים / הכשרה')}
    </div>
  );

  const renderExperience = () => (
    <div className="mt-4 space-y-2">
      {data.experience.map((item, index) => {
        const isOpen = openExperience === index;
        const title = [item.role, item.company].filter((x) => x && x.trim()).join(' · ');
        const period = [item.startYear, item.current ? 'היום' : item.endYear].filter((x) => x && String(x).trim()).join(' – ');
        return (
          <div key={index}>
            {accordionCard({
              isOpen,
              title,
              placeholder: `תפקיד ${index + 1}`,
              subtitle: period,
              onToggle: () => setOpenExperience(isOpen ? -1 : index),
              onEdit: () => setOpenExperience(index),
              onRemove: () => removeAccordionRow('experience', index, () => ({ ...emptyExperience, bullets: [''] }), openExperience, setOpenExperience),
              editLabel: `עריכת תפקיד ${index + 1}`,
              removeLabel: `הסרת תפקיד ${index + 1}`
            }, (
              <>
                <CompanyAutocomplete
                  ariaLabel={`שם מעסיק ${index + 1}`}
                  value={item.company}
                  onChange={(name) => patchList('experience', index, { company: name })}
                  placeholder="שם המעסיק, למשל: פירון משרד עורכי דין"
                  className={inputClass}
                  notifyOnType
                  persistOnCreate={false}
                />
                <input aria-label={`תפקיד ${index + 1}`} value={item.role}
                  onChange={(event) => patchList('experience', index, { role: event.target.value })}
                  placeholder="תפקיד, למשל: מתמחה" className={inputClass} />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <span className="mb-1 block text-xs font-bold text-gray-600">תחילת העסקה</span>
                    <div className="grid grid-cols-2 gap-2">
                      <input aria-label={`חודש התחלה ${index + 1}`} inputMode="numeric" value={item.startMonth}
                        onChange={(event) => patchList('experience', index, { startMonth: sanitizeMonth(event.target.value) })}
                        placeholder="חודש 1–12" className={smallInputClass} />
                      <input aria-label={`שנת התחלה תפקיד ${index + 1}`} inputMode="numeric" value={item.startYear}
                        onChange={(event) => patchList('experience', index, { startYear: sanitizeYear(event.target.value) })}
                        placeholder="שנה" className={smallInputClass} />
                    </div>
                  </div>
                  <div>
                    <span className="mb-1 block text-xs font-bold text-gray-600">סיום העסקה</span>
                    <div className="grid grid-cols-2 gap-2">
                      <input aria-label={`חודש סיום ${index + 1}`} inputMode="numeric" value={item.endMonth}
                        disabled={item.current}
                        onChange={(event) => patchList('experience', index, { endMonth: sanitizeMonth(event.target.value) })}
                        placeholder="חודש 1–12" className={`${smallInputClass} disabled:opacity-40`} />
                      <input aria-label={`שנת סיום תפקיד ${index + 1}`} inputMode="numeric" value={item.endYear}
                        disabled={item.current}
                        onChange={(event) => patchList('experience', index, { endYear: sanitizeYear(event.target.value) })}
                        placeholder="שנה" className={`${smallInputClass} disabled:opacity-40`} />
                    </div>
                  </div>
                </div>
                {(() => {
                  const rangeBad = yearOutOfRange(item.startYear) || (!item.current && yearOutOfRange(item.endYear));
                  const start = toNum(item.startYear);
                  const end = toNum(item.endYear);
                  const endBeforeStart = !item.current && start != null && end != null && end < start;
                  if (!rangeBad && !endBeforeStart) return null;
                  return (
                    <p className="text-xs text-amber-600">
                      {endBeforeStart ? 'שנת הסיום מוקדמת משנת ההתחלה.' : `בדקו את השנים — הערך נראה חריג (טווח סביר ${YEAR_MIN}–${CURRENT_YEAR}).`}
                    </p>
                  );
                })()}
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={item.current}
                    onChange={(event) => patchList('experience', index, { current: event.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-yellow-600 focus:ring-yellow-500" />
                  עדיין עובד/ת כאן (יוצג "היום")
                </label>
                <div className="space-y-2">
                  <div className="space-y-1">
                    <span className="block text-sm font-bold text-gray-700">במה עסקת במסגרת התפקיד?</span>
                    <p className="text-sm leading-6 text-gray-500">
                      כדאי לפרט בנקודות את תחומי האחריות, סוגי הפרויקטים, הלקוחות, המערכות וההישגים המרכזיים. המידע הזה חשוב למעסיקים ומאפשר להם להבין במהירות את מידת ההתאמה שלך למשרה.
                    </p>
                  </div>
                  {item.bullets.map((bullet, bulletIndex) => (
                    <div key={bulletIndex} className="flex items-start gap-2">
                      <span aria-hidden="true" className="mt-3 text-gray-400">•</span>
                      <textarea aria-label={`בולט ${bulletIndex + 1} בתפקיד ${index + 1}`} rows={2} maxLength={500}
                        value={bullet} onChange={(event) => patchBullet(index, bulletIndex, event.target.value)}
                        placeholder="רעיון מרכזי אחד, למשל: ניסוח כתבי טענות ומחקר פסיקה"
                        className={`resize-y leading-6 ${smallInputClass}`} />
                      {item.bullets.length > 1 && (
                        <button type="button" onClick={() => removeBullet(index, bulletIndex)}
                          aria-label={`הסרת בולט ${bulletIndex + 1} בתפקיד ${index + 1}`}
                          className="mt-2 text-gray-400 hover:text-red-600">×</button>
                      )}
                    </div>
                  ))}
                  {addButton(() => addBullet(index), '+ הוספת בולט')}
                </div>
              </>
            ))}
          </div>
        );
      })}
      {addButton(() => {
        addToList('experience', () => ({ ...emptyExperience, bullets: [''] }));
        setOpenExperience(data.experience.length);
      }, '+ הוספת מקום עבודה')}
    </div>
  );

  const renderRelevantSkills = () => (
    <div className="mt-4 space-y-2">
      <p className="text-xs text-gray-400">בחרו כישור מהרשימה. כישור שאינו במאגר יימחק אוטומטית. לחצו על כישור כדי לערוך את התיאור.</p>
      {data.relevantSkills.map((item, index) => {
        const isOpen = openSkill === index;
        const filled = Boolean(item.title.trim());
        return (
          <div key={index}>
            {accordionCard({
              isOpen,
              title: filled ? item.title : '',
              placeholder: `כישור ${index + 1}`,
              status: filled && (item.skillId
                ? <span className="shrink-0 text-xs text-green-600">✓ במאגר</span>
                : <span className="shrink-0 text-xs text-amber-600">⚠ לא במאגר</span>),
              onToggle: () => setOpenSkill(isOpen ? -1 : index),
              onEdit: () => setOpenSkill(index),
              onRemove: () => removeAccordionRow('relevantSkills', index, () => ({ ...emptySkill }), openSkill, setOpenSkill),
              editLabel: `עריכת כישור ${index + 1}`,
              removeLabel: `הסרת כישור ${index + 1}`
            }, (
              <>
                <CatalogAutocomplete
                  strict
                  ariaLabel={`שם הכישור ${index + 1}`}
                  value={item.title}
                  options={skillCatalog}
                  onChange={(name, id) => patchList('relevantSkills', index, { title: name, skillId: id ?? matchCatalogId(skillCatalog, name) })}
                  placeholder="בחרו כישור מהרשימה, למשל: בנקאות ומימון" className={inputClass} />
                <label className="block text-sm font-bold text-gray-700">מה הניסיון שלך בתחום זה?</label>
                <textarea aria-label={`תיאור הכישור ${index + 1}`} rows={2} maxLength={800}
                  value={item.description} onChange={(event) => patchList('relevantSkills', index, { description: event.target.value })}
                  placeholder="כדאי לתאר בקצרה את הניסיון שצברת בתחום, סוגי הפרויקטים או התהליכים שבהם היית מעורב/ת, תחומי האחריות וההישגים המרכזיים. פירוט זה מסייע למעסיקים להבין את עומק הידע והניסיון שלך. " className={`resize-y leading-6 ${inputClass}`} />
              </>
            ))}
          </div>
        );
      })}
      {addButton(() => {
        addToList('relevantSkills', () => ({ ...emptySkill }));
        setOpenSkill(data.relevantSkills.length);
      }, '+ הוספת כישור')}
    </div>
  );

  // One proficiency level per language. The server still stores reading/writing/
  // verbal separately, so mirror the chosen value into all three.
  const languageLevelSelect = (index) => {
    const current = data.languages[index];
    const value = current.verbal || current.writing || current.reading || '';
    return (
      <select aria-label={`רמה ${index + 1}`} value={value}
        onChange={(event) => patchList('languages', index,
          { reading: event.target.value, writing: event.target.value, verbal: event.target.value })}
        className={smallInputClass}>
        <option value="">רמה</option>
        {LANGUAGE_LEVELS.map((lvl) => <option key={lvl.value} value={lvl.value}>{lvl.label}</option>)}
      </select>
    );
  };

  const renderLanguages = () => (
    <div className="mt-4 space-y-4">
      {data.languages.map((item, index) => (
        <div key={index} className="grid gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-bold text-gray-700">שפה {index + 1}</span>
            {data.languages.length > 1 &&
              removeButton(() => removeFromList('languages', index, () => ({ ...emptyLanguage })), `הסרת שפה ${index + 1}`)}
          </div>
          <CatalogAutocomplete
            ariaLabel={`שפה ${index + 1}`}
            value={item.language}
            options={catalogs.languages}
            onChange={(name, id) => patchList('languages', index, { language: name, languageId: id ?? matchCatalogId(catalogs.languages, name) })}
            placeholder="שפה, למשל: עברית" className={smallInputClass} />
          {item.language.trim() && !item.languageId && (
            <p className="text-xs text-amber-600">בחרו שפה מהרשימה — שפה שאינה במאגר לא תישמר בפרופיל.</p>
          )}
          {languageLevelSelect(index)}
        </div>
      ))}
      {addButton(() => addToList('languages', () => ({ ...emptyLanguage })), '+ הוספת שפה')}
    </div>
  );

  const renderMilitary = () => (
    <div className="mt-4 space-y-4">
      {data.militaryService.map((item, index) => (
        <div key={index} className="grid gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-bold text-gray-700">שירות {index + 1}</span>
            {removeButton(() => removeFromList('militaryService', index, () => ({ ...emptyMilitary })), `הסרת שירות ${index + 1}`)}
          </div>
          <input aria-label={`יחידה ${index + 1}`} value={item.unit}
            onChange={(event) => patchList('militaryService', index, { unit: event.target.value })}
            placeholder="יחידה, למשל: חיל האוויר" className={inputClass} />
          <input aria-label={`תפקיד צבאי ${index + 1}`} value={item.role}
            onChange={(event) => patchList('militaryService', index, { role: event.target.value })}
            placeholder="תפקיד, למשל: קצינת מבצעים" className={inputClass} />
          <div className="grid grid-cols-2 gap-3">
            <input aria-label={`שנת גיוס ${index + 1}`} inputMode="numeric" value={item.startYear}
              onChange={(event) => patchList('militaryService', index, { startYear: sanitizeYear(event.target.value) })}
              placeholder="משנה" className={smallInputClass} />
            <input aria-label={`שנת שחרור ${index + 1}`} inputMode="numeric" value={item.endYear}
              onChange={(event) => patchList('militaryService', index, { endYear: sanitizeYear(event.target.value) })}
              placeholder="עד שנה" className={smallInputClass} />
          </div>
        </div>
      ))}
      {addButton(() => addToList('militaryService', () => ({ ...emptyMilitary })), '+ הוספת שירות')}
    </div>
  );

  const renderComputerSkills = () => (
    <div className="mt-4 space-y-3">
      {data.computerSkills.map((item, index) => (
        <div key={index} className="flex items-center gap-2">
          <input aria-label={`מיומנות מחשב ${index + 1}`} value={item}
            onChange={(event) => setData((current) => ({
              ...current,
              computerSkills: current.computerSkills.map((skill, i) => (i === index ? event.target.value : skill))
            }))}
            placeholder="למשל: Excel" className={smallInputClass} />
          {data.computerSkills.length > 1 && (
            <button type="button" aria-label={`הסרת מיומנות ${index + 1}`}
              onClick={() => setData((current) => {
                const next = current.computerSkills.filter((_, i) => i !== index);
                return { ...current, computerSkills: next.length ? next : [''] };
              })}
              className="text-gray-400 hover:text-red-600">×</button>
          )}
        </div>
      ))}
      {addButton(() => setData((current) => ({ ...current, computerSkills: [...current.computerSkills, ''] })), '+ הוספת כלי')}
    </div>
  );

  const renderCourses = () => (
    <div className="mt-4 space-y-3">
      {data.courses.map((item, index) => (
        <div key={index} className="flex items-center gap-2">
          <input aria-label={`שנת קורס ${index + 1}`} inputMode="numeric" value={item.year}
            onChange={(event) => patchList('courses', index, { year: sanitizeYear(event.target.value) })}
            placeholder="שנה" className={`${smallInputClass} max-w-[6rem]`} />
          <input aria-label={`שם קורס ${index + 1}`} value={item.name}
            onChange={(event) => patchList('courses', index, { name: event.target.value })}
            placeholder="שם הקורס / ההסמכה" className={smallInputClass} />
          {data.courses.length > 1 && (
            <button type="button" onClick={() => removeFromList('courses', index, () => ({ ...emptyCourse }))}
              aria-label={`הסרת קורס ${index + 1}`} className="text-gray-400 hover:text-red-600">×</button>
          )}
        </div>
      ))}
      {addButton(() => addToList('courses', () => ({ ...emptyCourse })), '+ הוספת קורס / הסמכה')}
    </div>
  );

  const renderers = {
    summary: renderSummary,
    education: renderEducation,
    experience: renderExperience,
    relevantSkills: renderRelevantSkills,
    languages: renderLanguages,
    militaryService: renderMilitary,
    computerSkills: renderComputerSkills,
    courses: renderCourses
  };

  // ---- step / review body --------------------------------------------------
  const body = isReview ? (
    <form onSubmit={createResume} className="space-y-4">
      <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-100">
        <h2 className="text-lg font-bold text-gray-900">כמעט סיימנו — נראה טוב? 👀</h2>
        <p className="mt-1 text-sm text-gray-500">אפשר לחזור ולערוך כל חלק. הקובץ ייווצר כמסמך Word ויישמר בפרופיל.</p>
        <div className="mt-4 space-y-3">
          {STEPS.map((s, index) => (
            <div key={s.name} className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
              <span className="flex items-center gap-2 text-sm font-bold text-gray-800">
                <span aria-hidden="true">{s.icon}</span>{s.title}
                {stepFilled(s.name)
                  ? <span className="text-green-600">✓</span>
                  : <span className="text-xs font-normal text-gray-400">— ריק</span>}
              </span>
              <button type="button" onClick={() => jumpTo(index)} className="text-sm font-bold text-yellow-700">עריכה</button>
            </div>
          ))}
        </div>
      </section>

      <button type="submit" disabled={isLoading} className="btn-primary w-full rounded-xl py-3.5 text-base disabled:opacity-50">
        {isLoading ? 'יוצר ושומר...' : 'סיום — יצירת קורות החיים'}
      </button>
      <button type="button" onClick={goBack} className="w-full rounded-xl py-2 text-sm font-bold text-gray-500 hover:text-gray-700">חזרה</button>
      <p className="text-center text-xs text-gray-500">הקובץ יישמר בפרופיל, ירד אוטומטית למכשיר כקובץ PDF, ותועברו למשרות</p>
    </form>
  ) : (
    <div className="space-y-4">
      <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-100">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-yellow-50 text-xl" aria-hidden="true">{step.icon}</span>
          <div>
            <h2 className="font-bold text-gray-900">
              {step.title}
              {step.required
                ? <span className="mr-1 text-sm font-normal text-red-500"> (חובה)</span>
                : <span className="mr-1 text-sm font-normal text-gray-400"> (אופציונלי)</span>}
            </h2>
            <p className="mt-0.5 text-sm text-gray-600">{step.intro}</p>
            {step.why && <p className="mt-1 text-xs text-gray-400">{step.why}</p>}
          </div>
        </div>
        {renderers[step.name]()}
      </section>

      <div className="flex items-center gap-3">
        {stepIndex > 0 && (
          <button type="button" onClick={goBack} className="btn-secondary flex-1 rounded-xl py-3 text-base">חזרה</button>
        )}
        <button type="button" onClick={goNext} className="btn-primary flex-1 rounded-xl py-3 text-base">
          {stepIndex === STEPS.length - 1 ? 'לסקירה' : 'המשך'}
        </button>
      </div>
      {hasContent && (
        <button
          type="button"
          onClick={() => jumpTo(STEPS.length)}
          className="flex w-full items-center justify-center gap-2 rounded-xl border border-yellow-200 bg-yellow-50 py-2.5 text-sm font-bold text-yellow-800 hover:bg-yellow-100"
        >
          מעבר לסיום ←
        </button>
      )}
      {!step.required && !stepFilled(step.name) && (
        <button type="button" onClick={goNext} className="w-full text-center text-sm font-bold text-gray-400 hover:text-gray-600">
          אין לי מה למלא כאן — דלג
        </button>
      )}
    </div>
  );

  // ---- layout --------------------------------------------------------------
  return (
    <Layout>
      <main className="min-h-[calc(100vh-4rem)] px-4 py-8 sm:px-6">
        <div className="mx-auto max-w-6xl">
          {error && <div className="mx-auto mb-4 max-w-2xl rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-red-700" role="alert">{error}</div>}

          <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(320px,0.8fr)]">
            <div className="space-y-4" data-testid="cv-editor">
              {header}
              {body}
            </div>

            <aside className="lg:sticky lg:top-6" data-testid="cv-preview" aria-label="תצוגה מקדימה של קורות החיים">
              <div className="mb-2 flex items-center justify-between px-1">
                <h2 className="font-bold text-gray-900"> קורות החיים שלך  </h2>
              </div>
              <div className="overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-gray-200">
                {hasContent ? (
                  <div className="max-h-[70vh] overflow-y-auto">
                    <ResumePreview model={model} identity={previewIdentity} />
                  </div>
                ) : (
                  <div className="flex min-h-[420px] flex-col items-center justify-center p-8 text-center text-gray-400">
                    <span className="text-5xl" aria-hidden="true">📄</span>
                    <p className="mt-4 font-bold text-gray-600">כאן תראו את קורות החיים האמיתיים</p>
                    <p className="mt-1 max-w-xs text-sm">מלאו לפחות חלק אחד והתוכן יופיע כאן.</p>
                  </div>
                )}
              </div>
            </aside>
          </div>
        </div>
      </main>
    </Layout>
  );
};

export default CreateCV;
